import { useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import { downloadCsv } from '../lib/annual-export';
import { extractApiError } from '../lib/api-client';
import { useT } from '../i18n';

/** A row read from the file: its line, and why it can't be imported, if so. */
export interface CsvImportRow {
  line: number;
  error?: { key: string; vars?: Record<string, string | number> };
}

// A few requests at a time: quick for a few hundred rows without flooding the API.
const PARALLEL = 4;

type Stage<Row> =
  | { kind: 'pick'; error?: string }
  | { kind: 'preview'; fileName: string; rows: Row[] }
  | { kind: 'importing'; done: number; total: number }
  | { kind: 'finished'; sent: number; failed: Array<{ row: Row; message: string }> };

/**
 * The "Impor massal" tab of a dialog: download the template, fill it in Excel,
 * upload it, see every row checked, then send the valid ones one request each.
 * Used for units and for resident invites; the caller supplies the parts that
 * differ (template, checks, columns, request and wording).
 */
export function CsvImportPanel<Row extends CsvImportRow>({
  templateFile,
  templateCsv,
  intro,
  step1Help,
  parse,
  columns,
  describeRow,
  send,
  runLabel,
  sentLabel,
  onClose,
  onImported,
}: {
  templateFile: string;
  templateCsv: () => string;
  /** Shown above the steps, e.g. a cluster picker. */
  intro?: ReactNode;
  step1Help: string;
  /** Checks a file's text; a string is an error to show instead of rows. */
  parse: (text: string) => Row[] | string;
  columns: Array<{ label: string; render: (row: Row) => ReactNode }>;
  /** How a row is named in the list of failures, e.g. "A2 No. 18". */
  describeRow: (row: Row) => string;
  send: (row: Row) => Promise<unknown>;
  runLabel: (count: number) => string;
  sentLabel: (count: number) => string;
  onClose: () => void;
  /** Some rows went through; the list behind the dialog should reload. */
  onImported: () => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage<Row>>({ kind: 'pick' });

  async function readFile(file: File) {
    const res = parse(await file.text());
    setStage(typeof res === 'string' ? { kind: 'pick', error: res } : { kind: 'preview', fileName: file.name, rows: res });
  }

  async function run(rows: Row[]) {
    const valid = rows.filter((r) => !r.error);
    const failed: Array<{ row: Row; message: string }> = [];
    let done = 0;
    setStage({ kind: 'importing', done, total: valid.length });

    const queue = [...valid];
    const worker = async () => {
      for (let row = queue.shift(); row; row = queue.shift()) {
        try {
          await send(row);
        } catch (e) {
          failed.push({ row, message: extractApiError(e).message });
        }
        done++;
        setStage({ kind: 'importing', done, total: valid.length });
      }
    };
    await Promise.all(Array.from({ length: PARALLEL }, worker));

    failed.sort((a, b) => a.row.line - b.row.line);
    const sent = valid.length - failed.length;
    setStage({ kind: 'finished', sent, failed });
    if (sent > 0) onImported();
  }

  const reset = () => {
    if (fileRef.current) fileRef.current.value = '';
    setStage({ kind: 'pick' });
  };

  if (stage.kind === 'pick') {
    return (
      <>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {intro}
            <Box>
              <Typography variant="subtitle2">{t('import.step1')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>{step1Help}</Typography>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadCsv(templateFile, templateCsv())}>
                {t('import.template')}
              </Button>
            </Box>
            <Box>
              <Typography variant="subtitle2">{t('import.step2')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>{t('import.step2Help')}</Typography>
              <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>
                {t('import.choose')}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void readFile(file);
                }}
              />
            </Box>
            {stage.error ? <Alert severity="error">{stage.error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </DialogActions>
      </>
    );
  }

  if (stage.kind === 'preview') {
    const ready = stage.rows.filter((r) => !r.error).length;
    const problems = stage.rows.length - ready;
    return (
      <>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {stage.fileName} · {t('import.summary', { ready, problems })}
            </Typography>
            {problems > 0 ? <Alert severity="warning">{t('import.problemsHelp')}</Alert> : null}
            <Box sx={{ maxHeight: 340, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('import.colRow')}</TableCell>
                    {columns.map((c) => <TableCell key={c.label}>{c.label}</TableCell>)}
                    <TableCell>{t('import.colStatus')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stage.rows.map((r) => (
                    <TableRow key={r.line}>
                      <TableCell sx={{ color: 'text.secondary' }}>{r.line}</TableCell>
                      {columns.map((c) => <TableCell key={c.label}>{c.render(r)}</TableCell>)}
                      <TableCell>
                        {r.error ? (
                          <Typography variant="caption" color="error.main">{t(r.error.key, r.error.vars)}</Typography>
                        ) : (
                          <Chip size="small" color="success" variant="outlined" label={t('import.ok')} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset}>{t('import.otherFile')}</Button>
          <Button variant="contained" disabled={ready === 0} onClick={() => void run(stage.rows)}>
            {runLabel(ready)}
          </Button>
        </DialogActions>
      </>
    );
  }

  if (stage.kind === 'importing') {
    return (
      <DialogContent>
        <Stack spacing={1.5} sx={{ py: 3 }}>
          <Typography variant="body2">{t('import.progress', { done: stage.done, total: stage.total })}</Typography>
          <LinearProgress variant="determinate" value={stage.total ? (stage.done / stage.total) * 100 : 100} />
        </Stack>
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity={stage.failed.length ? 'warning' : 'success'}>
            {sentLabel(stage.sent)}
            {stage.failed.length ? ` ${t('import.failedCount', { count: stage.failed.length })}` : ''}
          </Alert>
          {stage.failed.map(({ row, message }) => (
            <Typography key={row.line} variant="body2">
              {t('import.rowFailed', { line: row.line, what: describeRow(row) })} {message}
            </Typography>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={reset}>{t('import.again')}</Button>
        <Button variant="contained" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </>
  );
}
